import { create } from "zustand"
import { persist } from "zustand/middleware"
import type { ContinuityLock, Direction, Story, RenderArtifact, RenderProgress } from "@/types"

interface StudioState {
  // Auth
  identity: { id: string; email: string; creditMicros: number } | null
  billing: { plan: string; subscription?: boolean } | null
  capabilities: { creative: boolean; rendering: boolean; videoGeneration: boolean; finishing: boolean; youtube: boolean }
  youtubeConnected: boolean

  // Project
  cloudProjectId: string
  title: string
  concept: string
  logline: string

  // Story
  directions: Direction[]
  selectedDirectionIndices: number[]
  story: Story | null
  storyApproved: boolean
  storyHash: string

  // Plan
  quality: 0 | 1 | 2
  planApproved: boolean
  planHash: string

  // Continuity
  continuityLock: ContinuityLock | null

  // Production
  renderJobId: string
  renderJobStatus: string
  renderArtifact: RenderArtifact | null
  batchRunId: string
  batchStatus: "pending" | "running" | "completed" | "failed"
  shots: { name: string; detail: string; cost: number; status?: string }[]
  renderProgress: RenderProgress
  selectedTake: number
  takesApproved: boolean
  spent: number
  budget: number

  // Finish
  masterReady: boolean
  masterJobId: string
  masterJobStatus: string
  masterArtifact: RenderArtifact | null

  // Publish
  published: boolean

  // UI
  view: string
  stage: number

  // Actions
  setIdentity: (identity: StudioState["identity"]) => void
  setBilling: (billing: StudioState["billing"]) => void
  setCapabilities: (capabilities: StudioState["capabilities"]) => void
  setYoutubeConnected: (connected: boolean) => void
  setProject: (projectId: string, title: string, concept: string) => void
  setDirections: (directions: Direction[]) => void
  toggleDirection: (index: number) => void
  setStory: (story: Story | null) => void
  setStoryApproved: (approved: boolean) => void
  setStoryHash: (hash: string) => void
  setQuality: (quality: 0 | 1 | 2) => void
  setPlanApproved: (approved: boolean) => void
  setPlanHash: (hash: string) => void
  setContinuityLock: (continuity: ContinuityLock | null) => void
  setRenderJob: (jobId: string, status: string, artifact?: RenderArtifact | null) => void
  setBatchStatus: (batchRunId: string, status: StudioState["batchStatus"]) => void
  setShots: (shots: StudioState["shots"]) => void
  setRenderProgress: (progress: RenderProgress) => void
  setSelectedTake: (take: number) => void
  setTakesApproved: (approved: boolean) => void
  setSpent: (spent: number) => void
  setBudget: (budget: number) => void
  setMasterReady: (ready: boolean) => void
  setMasterJob: (jobId: string, status: string, artifact?: RenderArtifact | null) => void
  setPublished: (published: boolean) => void
  setView: (view: string) => void
  setStage: (stage: number) => void
  resetProject: () => void
  updateTitle: (title: string) => void
  updateLogline: (logline: string) => void
}

const emptyCapabilities = () => ({
  creative: false,
  rendering: false,
  videoGeneration: false,
  finishing: false,
  youtube: false,
})

export const useStudioStore = create<StudioState>()(
  persist(
    (set) => ({
      identity: null,
      billing: null,
      capabilities: emptyCapabilities(),
      youtubeConnected: false,
      cloudProjectId: "",
      title: "Untitled project",
      concept: "",
      logline: "",
      directions: [],
      selectedDirectionIndices: [],
      story: null,
      storyApproved: false,
      storyHash: "",
      quality: 0,
      planApproved: false,
      planHash: "",
      continuityLock: null,
      renderJobId: "",
      renderJobStatus: "",
      renderArtifact: null,
      batchRunId: "",
      batchStatus: "pending",
      shots: [],
      renderProgress: { completed: 0, total: 0, etaSeconds: 0, confidence: 0 },
      selectedTake: 2,
      takesApproved: false,
      spent: 0,
      budget: 8,
      masterReady: false,
      masterJobId: "",
      masterJobStatus: "",
      masterArtifact: null,
      published: false,
      view: "home",
      stage: 0,

      setIdentity: (identity) => set({ identity }),
      setBilling: (billing) => set({ billing }),
      setCapabilities: (capabilities) => set({ capabilities }),
      setYoutubeConnected: (youtubeConnected) => set({ youtubeConnected }),
      setProject: (cloudProjectId, title, concept) => set({ cloudProjectId, title, concept }),
      setDirections: (directions) => set({ directions }),
      toggleDirection: (index) =>
        set((state) => ({
          selectedDirectionIndices: state.selectedDirectionIndices.includes(index)
            ? state.selectedDirectionIndices.filter((i) => i !== index)
            : [...state.selectedDirectionIndices, index],
        })),
      setStory: (story) => set({ story, continuityLock: null }),
      setStoryApproved: (storyApproved) => set({ storyApproved }),
      setStoryHash: (storyHash) => set({ storyHash }),
      setQuality: (quality) => set({ quality }),
      setPlanApproved: (planApproved) => set({ planApproved }),
      setPlanHash: (planHash) => set({ planHash }),
      setContinuityLock: (continuityLock) => set({ continuityLock }),
      setRenderJob: (renderJobId, renderJobStatus, renderArtifact) => set({ renderJobId, renderJobStatus, renderArtifact: renderArtifact ?? null }),
      setBatchStatus: (batchRunId, batchStatus) => set({ batchRunId, batchStatus }),
      setShots: (shots) => set({ shots }),
      setRenderProgress: (renderProgress) => set({ renderProgress }),
      setSelectedTake: (selectedTake) => set({ selectedTake }),
      setTakesApproved: (takesApproved) => set({ takesApproved }),
      setSpent: (spent) => set({ spent }),
      setBudget: (budget) => set({ budget }),
      setMasterReady: (masterReady) => set({ masterReady }),
      setMasterJob: (masterJobId, masterJobStatus, masterArtifact) => set({ masterJobId, masterJobStatus, masterArtifact: masterArtifact ?? null }),
      setPublished: (published) => set({ published }),
      setView: (view) => set({ view }),
      setStage: (stage) => set({ stage }),
      resetProject: () =>
        set({
          cloudProjectId: "",
          title: "Untitled project",
          concept: "",
          logline: "",
          directions: [],
          selectedDirectionIndices: [],
          story: null,
          storyApproved: false,
          storyHash: "",
          quality: 0,
          planApproved: false,
          planHash: "",
          continuityLock: null,
          renderJobId: "",
          renderJobStatus: "",
          renderArtifact: null,
          batchRunId: "",
          batchStatus: "pending",
          shots: [],
          renderProgress: { completed: 0, total: 0, etaSeconds: 0, confidence: 0 },
          selectedTake: 2,
          takesApproved: false,
          spent: 0,
          masterReady: false,
          masterJobId: "",
          masterJobStatus: "",
          masterArtifact: null,
          published: false,
          view: "home",
          stage: 0,
        }),
      updateTitle: (title) => set({ title }),
      updateLogline: (logline) => set({ logline }),
    }),
    {
      name: "atlas-studio-state-v3",
      partialize: (state) => ({
        cloudProjectId: state.cloudProjectId,
        title: state.title,
        concept: state.concept,
        logline: state.logline,
        directions: state.directions,
        selectedDirectionIndices: state.selectedDirectionIndices,
        story: state.story,
        storyApproved: state.storyApproved,
        storyHash: state.storyHash,
        quality: state.quality,
        planApproved: state.planApproved,
        planHash: state.planHash,
        continuityLock: state.continuityLock,
        renderJobId: state.renderJobId,
        renderJobStatus: state.renderJobStatus,
        renderArtifact: state.renderArtifact,
        batchRunId: state.batchRunId,
        batchStatus: state.batchStatus,
        renderProgress: state.renderProgress,
        selectedTake: state.selectedTake,
        takesApproved: state.takesApproved,
        spent: state.spent,
        budget: state.budget,
        masterReady: state.masterReady,
        masterArtifact: state.masterArtifact,
        published: state.published,
        view: state.view,
        stage: state.stage,
      }),
    }
  )
)
